import React, { useMemo } from 'react'
import Aside from './Aside'
import style from './Main.module.scss'
import commonStyle from '../Costco.module.scss'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import { EcommerceResponse, IEcommerceQuestionViewProps } from '../../types'
import ProductPreview from './ProductPreview'
import MultipleCarousel from './MultipleCarousel'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

interface IMainProps {
  schema: IEcommerceQuestionViewProps['schema']
  response: EcommerceResponse
  findability: boolean
  resetCenterElementRef: () => void
  registerCardRef: (id: string, element: HTMLDivElement | null) => void
  onItemSelect: (index: number) => void
  onAddToCart: (qnt: number, index: number) => void
  scrollTop: () => void
}

const Main = ({
  schema,
  response,
  findability,
  resetCenterElementRef,
  registerCardRef,
  onItemSelect,
  onAddToCart,
  scrollTop
}: IMainProps) => {
  const topDeals = useMemo(() => schema.answers.filter(answer => answer.featured), [schema])
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  return (
    <section className={`${style.main} ${viewport.mobile ? style.mainMobile : ''}`}>
      <Aside />
      <div className={style.content}>
        {topDeals.length > 0 && (
          <div className={style.topDeals} onClick={resetCenterElementRef}>
            <div className={style.topDealsTitle}>{t('questions', 'ec', 'costco', 'topDeals')}</div>
            <MultipleCarousel
              slidesPerView={viewport.mobile ? 2 : 5}
              showNavigation={viewport.mobile ? false : topDeals.length > 5}
            >
              {schema.answers
                .filter(answer => !answer.na)
                .map((answer, index) =>
                  answer.featured ? (
                    <ProductPreview
                      key={answer.id}
                      data-testid="SW-EC-CarouselItem"
                      schema={schema}
                      quantity={response.answers[index].v}
                      fit={viewport.mobile ? 2 : 5}
                      findability={findability}
                      answer={answer}
                      index={index}
                      disabled={'focus' == schema.mode_type && !answer.focused}
                      showButtons={false}
                      showSponsoredLabel={false}
                      onItemSelect={onItemSelect}
                      onAddToCart={onAddToCart}
                    />
                  ) : null
                )}
            </MultipleCarousel>
          </div>
        )}
        {viewport.mobile ? (
          <div className={style.mainFiltersMobile}>
            <div
              className={style.results}
              dangerouslySetInnerHTML={{
                __html: t<(results: number, condition?: string) => string>(
                  'questions',
                  'ec',
                  'costco',
                  'results'
                )(schema.answers.length)
              }}
            />
            <EmptyLink color="#0060A9">
              <div className={style.sort}>
                <span className={style.text}>{t('questions', 'ec', 'costco', 'byBest')}</span>
                <i className="pi pi-chevron-down" />
              </div>
            </EmptyLink>
            <EmptyLink color="#0060A9">
              <div className={style.filter}>
                <i className="pi pi-sliders-h" />
                {t('questions', 'ec', 'costco', 'filter')}
              </div>
            </EmptyLink>
          </div>
        ) : (
          <div className={style.mainFilters}>
            <div className={style.viewContainer}>
              <div
                className={style.results}
                dangerouslySetInnerHTML={{
                  __html: t<(results: number, condition?: string) => string>(
                    'questions',
                    'ec',
                    'costco',
                    'results'
                  )(schema.answers.length, schema.search_condition)
                }}
              />
            </div>
            <div className={style.sortContainer}>
              <div className={style.sortTitle}>{t('questions', 'ec', 'costco', 'sortBy')}</div>
              <div className={style.sortSelect}>
                <div className={style.sortSelectText}>
                  <EmptyLink color="#0060A9">
                    <div className={style.text}>{t('questions', 'ec', 'costco', 'bestMatch')}</div>
                  </EmptyLink>
                </div>
                <EmptyLink color="#0060A9">
                  <i className={`${commonStyle.costcoIcon} ${commonStyle.arrowDown} ${style.selectTrigger}`} />
                </EmptyLink>
              </div>
            </div>
          </div>
        )}
        <div className={style.products}>
          {schema.answers.map((answer, index) =>
            !answer.parent_product && !answer.na ? (
              <ProductPreview
                key={answer.id}
                data-testid="SW-EC-Item"
                schema={schema}
                quantity={response.answers[index].v}
                fit={viewport.mobile ? 1 : schema.products_per_row}
                findability={findability}
                answer={answer}
                index={index}
                disabled={'focus' == schema.mode_type && !answer.focused}
                onItemSelect={onItemSelect}
                onAddToCart={onAddToCart}
                ref={element => registerCardRef(answer.id, element)}
              />
            ) : null
          )}
        </div>
        <div className={style.scrollTopContainer}>
          <button className={style.scrollTop} onClick={scrollTop}>
            {t('questions', 'ec', 'costco', 'top')}
          </button>
        </div>
      </div>
    </section>
  )
}
export default Main
